import "../css/pages/AddCustomer.css";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MasterDropdown from "../components/MasterDropdown";
import DocumentCameraModal from "../components/DocumentCameraModal";
import DateInput from "../components/DateInput";
import {
  createCustomer,
  uploadCustomerIdFront,
  scanDriversLicense,
  getCompanies,
  createCompany,
  getCustomers,
  cropDocumentImage,
} from "../api/services";
import {
  DocumentImageError,
  hasReadableScanData,
  isLowConfidence,
} from "../utils/documentImage";
import toast from "react-hot-toast";
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
];
const EYE_COLORS = [
  "BLK",
  "BLU",
  "BRN",
  "GRY",
  "GRN",
  "HAZ",
  "MAR",
  "MUL",
  "PNK",
  "UNK",
];
const HAIR_COLORS = [
  "BAL",
  "BLK",
  "BLN",
  "BLU",
  "BRN",
  "GRY",
  "GRN",
  "MUL",
  "ORG",
  "PNK",
  "RED",
  "SDY",
  "WHI",
  "UNK",
];

const defaultForm = {
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  sex: "",
  height: "",
  weight: "",
  eyeColor: "",
  hairColor: "",
  ssn: "",
  address: {
    street: "",
    apt: "",
    city: "",
    state: "AL",
    zip: "",
    country: "USA",
  },
  identifications: [
    {
      type: "Driver License",
      idNumber: "",
      issuedState: "AL",
      issuedClass: "D",
      expiryDate: "",
      issueDate: "",
      restrictions: "",
      endorsements: "",
    },
  ],
  contacts: [{ type: "Cell Phone", value: "+1 " }],
  employer: { name: "", address: "", phone: "" },
  notes: "",
};

// Format phone as (251) 555-0123 from raw digits
const formatPhone = (raw = "") => {
  // If user typed +1 prefix, strip it for formatting then re-add
  const stripped = raw.replace(/^\+1\s*/, "").replace(/\D/g, "");
  if (!stripped) return "+1 ";
  const d = stripped.slice(0, 10);
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

// Display date as MM-DD-YYYY from YYYY-MM-DD (for display next to inputs)
const isoToDisplay = (iso = "") => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}-${d}-${y}`;
};

const SectionHeader = ({ icon, title, subtitle }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 18,
      paddingBottom: 10,
      borderBottom: "2px solid var(--gray-3)",
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        background: "var(--primary-light)",
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
      }}
    >
      {icon}
    </div>
    <div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 15,
        }}
      >
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11.5, color: "var(--gray-1)", marginTop: 1 }}>
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

const Field = ({ label, required, children, span }) => (
  <div
    className="form-group"
    style={{ gridColumn: span ? `span ${span}` : undefined, margin: 0 }}
  >
    <label className="form-label">
      {label}
      {required && (
        <span style={{ color: "var(--danger)", marginLeft: 3 }}>*</span>
      )}
    </label>
    {children}
  </div>
);

// ── Quick Add Company Modal ───────────────────────────────────────────────────
function AddCompanyModal({ onClose, onSaved }) {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await createCompany({ name: name.trim(), address, phone });
      toast.success(`✅ Company "${name}" added!`);
      onSaved(res.data.data);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add company");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 14,
          padding: 28,
          width: 420,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 800,
            fontSize: 17,
            marginBottom: 18,
          }}
        >
          🏢 Add New Company
        </div>
        <div className="form-group">
          <label className="form-label">
            Company Name <span style={{ color: "var(--danger)" }}>*</span>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Graham Shrimp Company"
            autoFocus
          />
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input
            className="form-control"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="PO Box 223, Bayou La Batre, AL 36509"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone</label>
          <input
            className="form-control"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(xxx) xxx-xxxx"
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button
            className="btn btn-primary"
            style={{ flex: 1 }}
            onClick={save}
            disabled={saving}
          >
            {saving ? "Saving..." : "✅ Save Company"}
          </button>
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * AddCustomer Page Component.
 * Customer intake form page supporting driver's license OCR scanning, image cropping, and company master lookup.
 *
 * @component
 * @returns {JSX.Element} AddCustomer view page.
 */
export default function AddCustomer() {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [idPhotoFile, setIdPhotoFile] = useState(null);
  const [idPhotoPreview, setIdPhotoPreview] = useState(null);
  const [showIdCamera, setShowIdCamera] = useState(false);
  const idPhotoRef = useRef();

  const loadCompanies = () =>
    getCompanies({ isBlocked: "false", limit: 9999 })
      .then((r) => setCompanyList(r.data.data || []))
      .catch(() => {});

  useEffect(() => {
    loadCompanies();
  }, []);

  const [idDuplicate, setIdDuplicate] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [existingCustomer, setExistingCustomer] = useState(null); // {id, name} when scan finds a match

  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));
  const setAddr = (field, val) =>
    setForm((f) => ({ ...f, address: { ...f.address, [field]: val } }));
  const setEmpl = (field, val) =>
    setForm((f) => ({ ...f, employer: { ...f.employer, [field]: val } }));
  const setId = (i, field, val) => {
    const ids = [...form.identifications];
    ids[i] = { ...ids[i], [field]: val };
    setForm((f) => ({ ...f, identifications: ids }));
    if (field === "type" || field === "idNumber") setIdDuplicate(null);
  };
  const setContact = (i, field, val) => {
    const c = [...form.contacts];
    c[i] = { ...c[i], [field]: val };
    setForm((f) => ({ ...f, contacts: c }));
  };

  // Phone input handler — auto-format as (251) 555-0123 with +1 prefix
  const handlePhoneChange = (i, raw) => {
    const formatted = formatPhone(raw);
    setContact(i, "value", formatted);
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const applyExtractedData = (d) => {
    setForm((f) => {
      const updated = { ...f };
      if (d.firstName) updated.firstName = d.firstName.trim();
      if (d.middleName) updated.middleName = d.middleName.trim();
      if (d.lastName) updated.lastName = d.lastName.trim();
      if (d.dateOfBirth) updated.dateOfBirth = d.dateOfBirth;
      if (d.sex) updated.sex = d.sex === "M" ? "M" : d.sex === "F" ? "F" : "";
      if (d.height) updated.height = d.height;
      if (d.weight) updated.weight = String(d.weight || "").replace(/\D/g, "");
      const normalizeColor = (c = "") => {
        const code = c.toUpperCase().slice(0, 3);
        return code === "BRO" ? "BRN" : code;
      };
      if (d.eyeColor) updated.eyeColor = normalizeColor(d.eyeColor);
      if (d.hairColor) updated.hairColor = normalizeColor(d.hairColor);

      if (d.address) {
        updated.address = {
          street: d.address.street || f.address.street,
          apt: d.address.apt || f.address.apt,
          city: d.address.city || f.address.city,
          state: d.address.state || f.address.state,
          zip: d.address.zip || f.address.zip,
          country: d.address.country || "USA",
        };
      }

      const ids = [...f.identifications];
      ids[0] = {
        ...ids[0],
        type: d.idType || ids[0].type,
        idNumber: d.idNumber || ids[0].idNumber,
        issuedState: d.issuedState || ids[0].issuedState,
        issuedClass: d.issuedClass || ids[0].issuedClass,
        expiryDate: d.expiryDate || ids[0].expiryDate,
        issueDate: d.issueDate || ids[0].issueDate,
        restrictions: d.restrictions || ids[0].restrictions,
        endorsements: d.endorsements || ids[0].endorsements,
      };
      updated.identifications = ids;
      return updated;
    });
  };

  // After a successful scan, check if this ID number already exists in the DB.
  // If found → show the "Customer Found" modal instead of continuing with the form.
  const checkExistingCustomerByIdNumber = async (idNumber) => {
    if (!idNumber) return false;
    try {
      const res = await getCustomers({ search: idNumber, limit: 5 });
      const matches = (res.data?.data || []).filter((c) =>
        c.identifications?.some(
          (id) =>
            id.idNumber?.trim().toLowerCase() === idNumber.trim().toLowerCase(),
        ),
      );
      if (matches.length > 0) {
        const c = matches[0];
        const name = [c.firstName, c.middleInitial, c.lastName]
          .filter(Boolean)
          .join(" ");
        setExistingCustomer({ id: c._id, name });
        return true; // found — caller should stop
      }
    } catch (err) {
      console.warn("Duplicate-check failed:", err);
    }
    return false;
  };

  const legacyHandleIdPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setIdPhotoFile(file);
    setIdPhotoPreview(URL.createObjectURL(file));
    setScanResult(null);
    setScanning(true);
    const toastId = toast.loading("🔍 Reading ID — auto-filling form...");
    try {
      const base64 = await fileToBase64(file);
      const res = await scanDriversLicense(base64, file.type || "image/jpeg");
      const d = res.data?.data;
      if (d && Object.keys(d).length > 0) {
        // Check if this ID already exists in DB before filling the form
        toast.dismiss(toastId);
        const found = await checkExistingCustomerByIdNumber(d.idNumber);
        if (found) {
          setScanning(false);
          return;
        }
        applyExtractedData(d);
        const filled = [];
        if (d.firstName || d.lastName)
          filled.push(
            `Name: ${[d.firstName, d.middleName, d.lastName].filter(Boolean).join(" ")}`,
          );
        if (d.idNumber) filled.push(`License #: ${d.idNumber}`);
        if (d.dateOfBirth)
          filled.push(
            `DOB: ${new Date(d.dateOfBirth + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/Chicago" })}`,
          );
        if (d.expiryDate)
          filled.push(
            `Expires: ${new Date(d.expiryDate + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/Chicago" })}`,
          );
        if (d.address?.street)
          filled.push(
            `Address: ${d.address.street}, ${d.address.city}, ${d.address.state}`,
          );
        setScanResult({ filled, idType: d.idType || "ID" });
        toast.success(`✅ ${d.idType || "ID"} scanned! Review fields below.`, {
          duration: 4000,
        });
      } else {
        toast.error("Could not read ID fields. Please fill in manually.", {
          id: toastId,
        });
      }
    } catch (err) {
      toast.error("Scan failed. Please fill in manually.", { id: toastId });
    } finally {
      setScanning(false);
    }
  };
  void legacyHandleIdPhoto;

  const scanPreparedId = async (prepared) => {
    const first = await scanDriversLicense(prepared.base64, prepared.mimeType);
    const firstData = first.data?.data;
    if (hasReadableScanData(firstData) && !isLowConfidence(firstData))
      return firstData;

    console.warn("ID OCR low confidence; retrying normalized image", {
      firstData,
      diagnostics: prepared.diagnostics,
    });
    const retry = await scanDriversLicense(
      prepared.fallbackBase64,
      prepared.mimeType,
    );
    return retry.data?.data || firstData;
  };
  void scanPreparedId;

  const scanIdFile = async (file, source = "gallery") => {
    if (!file) return;
    setScanResult(null);
    setScanning(true);
    const toastId = toast.loading("Reading ID - auto-filling form...");

    // Set preview immediately so user sees their photo
    if (idPhotoPreview) URL.revokeObjectURL(idPhotoPreview);
    setIdPhotoFile(file);
    setIdPhotoPreview(URL.createObjectURL(file));

    try {
      // Step 1 — CROP: send the raw ID photo to the Python crop API (same
      // service used for checks and the profile-page ID upload). Works
      // identically on mobile and desktop.
      let croppedFile = file;
      try {
        const rawBase64 = await fileToBase64(file);
        const cropRes = await cropDocumentImage(rawBase64, file.type || "image/jpeg");
        const cd = cropRes.data;
        if (cd?.success && cd?.image) {
          const mimeType = cd.mimeType || "image/jpeg";
          const blob = await fetch(`data:${mimeType};base64,${cd.image}`).then(
            (r) => r.blob(),
          );
          const ext = mimeType.includes("png") ? "png" : "jpg";
          croppedFile = new File(
            [blob],
            `id-front-${Date.now()}-cropped.${ext}`,
            { type: mimeType, lastModified: Date.now() },
          );
        }
      } catch (cropErr) {
        console.warn("ID crop failed, using original image:", cropErr.message);
      }

      if (idPhotoPreview) URL.revokeObjectURL(idPhotoPreview);
      setIdPhotoFile(croppedFile);
      setIdPhotoPreview(URL.createObjectURL(croppedFile));

      const base64 = await fileToBase64(croppedFile);
      const mimeType = croppedFile.type || "image/jpeg";

      // Step 2 — CHECK: run OCR on the cropped image
      const res = await scanDriversLicense(base64, mimeType);
      const d = res.data?.data;

      if (hasReadableScanData(d)) {
        // Check if this ID already exists in DB before filling the form
        toast.dismiss(toastId);
        const found = await checkExistingCustomerByIdNumber(d.idNumber);
        if (found) {
          setScanning(false);
          return;
        }
        applyExtractedData(d);
        const filled = [];
        if (d.firstName || d.lastName)
          filled.push(
            `Name: ${[d.firstName, d.middleName, d.lastName].filter(Boolean).join(" ")}`,
          );
        if (d.idNumber) filled.push(`License #: ${d.idNumber}`);
        if (d.dateOfBirth)
          filled.push(
            `DOB: ${new Date(d.dateOfBirth + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/Chicago" })}`,
          );
        if (d.expiryDate)
          filled.push(
            `Expires: ${new Date(d.expiryDate + "T12:00:00").toLocaleDateString("en-US", { timeZone: "America/Chicago" })}`,
          );
        if (d.address?.street)
          filled.push(
            `Address: ${d.address.street}, ${d.address.city}, ${d.address.state}`,
          );
        setScanResult({ filled, idType: d.idType || "ID" });
        toast.success(`${d.idType || "ID"} scanned! Review fields below.`, {
          duration: 4000,
        });
      } else {
        toast.error("Could not read ID fields. Please fill in manually.", {
          id: toastId,
        });
      }
    } catch (err) {
      console.error("ID scan error:", {
        source,
        error: err,
        response: err.response?.data,
      });
      let message = "Scan failed. Please fill in manually.";
      if (err instanceof DocumentImageError && err.code === "BLURRY_IMAGE") {
        message = "Image is too blurry. Hold steady and retake the photo.";
      } else if (!navigator.onLine) {
        message =
          "No internet connection. Please check your network and try again.";
      } else if (err?.response?.status >= 500) {
        message = "Server error. Please try again in a moment.";
      }
      toast.error(message, { id: toastId });
    } finally {
      setScanning(false);
    }
  };

  const handleIdPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    await scanIdFile(file, "gallery");
  };

  const addId = () =>
    setForm((f) => ({
      ...f,
      identifications: [
        ...f.identifications,
        {
          type: "State ID",
          idNumber: "",
          issuedState: "AL",
          issuedClass: "",
          expiryDate: "",
          issueDate: "",
          restrictions: "",
          endorsements: "",
        },
      ],
    }));
  const removeId = (i) =>
    setForm((f) => ({
      ...f,
      identifications: f.identifications.filter((_, x) => x !== i),
    }));
  const addContact = () =>
    setForm((f) => ({
      ...f,
      contacts: [...f.contacts, { type: "Cell Phone", value: "+1 " }],
    }));
  const removeContact = (i) =>
    setForm((f) => ({ ...f, contacts: f.contacts.filter((_, x) => x !== i) }));

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName) {
      toast.error("First and last name required");
      return;
    }
    if (!form.dateOfBirth) {
      toast.error("Date of birth required");
      return;
    }
    if (!form.identifications[0]?.idNumber) {
      toast.error("At least one ID number required");
      return;
    }

    // Validate contacts — remove blank ones, but keep at least valid ones
    const cleanContacts = form.contacts.filter((c) => {
      const v = c.value?.trim() || "";
      return v && v !== "+1" && v !== "+1 ";
    });
    if (cleanContacts.length === 0) {
      toast.error("Please enter at least one contact number");
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form, contacts: cleanContacts };
      const res = await createCustomer(payload);
      const customerId = res.data.data._id;
      if (idPhotoFile) {
        try {
          await uploadCustomerIdFront(customerId, idPhotoFile);
        } catch {
          toast.error("Customer saved but ID photo upload failed.");
        }
      }
      toast.success("Customer added successfully!");
      navigate(`/customers/${customerId}`);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create customer";
      const dupId = err.response?.data?.duplicateCustomerId;
      if (dupId) setIdDuplicate({ message: msg, customerId: dupId });
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const id0 = form.identifications[0] || {};

  return (
    <Layout title="Add New Customer">
      <DocumentCameraModal
        open={showIdCamera}
        title="Scan ID"
        label="ID"
        aspectRatio={1.6}
        onClose={() => setShowIdCamera(false)}
        onCapture={(file) => scanIdFile(file, "camera")}
      />
      {showAddCompany && (
        <AddCompanyModal
          onClose={() => setShowAddCompany(false)}
          onSaved={(company) => {
            setShowAddCompany(false);
            loadCompanies();
            setEmpl("name", company.name);
          }}
        />
      )}

      <div className="flex items-center gap-3 mb-4">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate("/customers")}
        >
          ← Back
        </button>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 20,
            fontWeight: 800,
          }}
        >
          ➕ New Customer Intake
        </h2>
        <span
          style={{
            fontSize: 12,
            color: "var(--gray-1)",
            background: "var(--gray-4)",
            padding: "3px 10px",
            borderRadius: 20,
          }}
        >
          Fill from Driver's License + ID
        </span>
      </div>

      {/* ── EXISTING CUSTOMER FOUND (scan matched a DB record) ── */}
      {existingCustomer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 16,
              padding: 32,
              width: 420,
              boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: 18,
                marginBottom: 8,
              }}
            >
              Customer Already Exists
            </div>
            <div
              style={{ fontSize: 14, color: "var(--gray-1)", marginBottom: 6 }}
            >
              This ID belongs to an existing customer:
            </div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 17,
                color: "var(--primary)",
                marginBottom: 24,
              }}
            >
              {existingCustomer.name}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                className="btn btn-primary"
                onClick={() =>
                  navigate(`/cheques/new?customerId=${existingCustomer.id}`)
                }
              >
                💵 Cash a Check for this Customer
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setExistingCustomer(null)}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {idDuplicate && (
        <div
          style={{
            background: "#fef2f2",
            border: "2px solid #dc2626",
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: "#dc2626", fontSize: 14 }}>
              ⚠️ Duplicate ID Detected
            </div>
            <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>
              {idDuplicate.message}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              className="btn btn-sm"
              style={{ background: "#dc2626", color: "white", border: "none" }}
              onClick={() => navigate(`/customers/${idDuplicate.customerId}`)}
            >
              View Existing →
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setIdDuplicate(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="customer-intake-grid">
        {/* ── LEFT COLUMN ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* DRIVER'S LICENSE SCAN */}
          <div className="card">
            <div
              className="card-header"
              style={{
                background: "linear-gradient(135deg,#1a3a5c,#1a5c8a)",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h3 style={{ color: "white" }}>🪪 Driver's License / State ID</h3>
              <span style={{ fontSize: 11.5, color: "rgba(255,255,255,0.6)" }}>
                Copy exactly as shown on the card
              </span>
            </div>
            <div
              className="card-body"
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "flex-start",
                  padding: "14px",
                  background: scanning ? "#f0fdf4" : "var(--info-light)",
                  borderRadius: 10,
                  border: `1.5px dashed ${scanning ? "#16a34a" : "#3b82f6"}`,
                  transition: "all 0.3s",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color: scanning ? "#16a34a" : "var(--info)",
                      marginBottom: 6,
                    }}
                  >
                    {scanning
                      ? "🤖 AI Scanning ID..."
                      : "📷 SCAN DRIVER'S LICENSE / ID"}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--gray-1)",
                      marginBottom: 8,
                    }}
                  >
                    {scanning
                      ? "Reading ID fields — auto-filling form now..."
                      : "Take a photo of the ID card — all fields will be auto-filled using AI."}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={idPhotoRef}
                    onChange={handleIdPhoto}
                    style={{ display: "none" }}
                  />
                  <input
                    type="file"
                    accept="image/*"
                    id="idPhotoGallery"
                    onChange={handleIdPhoto}
                    style={{ display: "none" }}
                  />
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ fontSize: 15, padding: "10px 20px" }}
                      onClick={() => setShowIdCamera(true)}
                      disabled={scanning}
                    >
                      📷 Scan ID
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => idPhotoRef.current.click()}
                      disabled={scanning}
                    >
                      🖼 Choose from Gallery
                    </button>
                    {idPhotoPreview && !scanning && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          setIdPhotoPreview(null);
                          setIdPhotoFile(null);
                          setScanResult(null);
                        }}
                      >
                        ✕ Clear
                      </button>
                    )}
                  </div>
                  {scanResult && !scanning && (
                    <div
                      style={{
                        marginTop: 10,
                        background: "#f0fdf4",
                        border: "1.5px solid #86efac",
                        borderRadius: 8,
                        padding: "10px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 700,
                          color: "#16a34a",
                          marginBottom: 7,
                        }}
                      >
                        ✅ Auto-filled from {scanResult.idType}:
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "5px 12px",
                        }}
                      >
                        {scanResult.filled.map((f, i) => (
                          <div
                            key={i}
                            style={{
                              fontSize: 12,
                              color: "#166534",
                              background: "#dcfce7",
                              borderRadius: 5,
                              padding: "3px 9px",
                              fontFamily: "monospace",
                              fontWeight: 600,
                            }}
                          >
                            ✓ {f}
                          </div>
                        ))}
                      </div>
                      <div
                        style={{ fontSize: 11, color: "#6b7280", marginTop: 7 }}
                      >
                        ⚠️ Always verify auto-filled fields match the physical
                        ID before saving.
                      </div>
                    </div>
                  )}
                </div>
                {idPhotoPreview && (
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <img
                      src={idPhotoPreview}
                      alt="ID"
                      style={{
                        width: 140,
                        height: 95,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: `2px solid ${scanning ? "#16a34a" : "var(--info)"}`,
                      }}
                    />
                    {scanning && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background: "rgba(22,163,74,0.18)",
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 28,
                        }}
                      >
                        🔍
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ID Cards */}
              {form.identifications.map((id, i) => (
                <div
                  key={i}
                  style={{
                    padding: 16,
                    background: "var(--gray-4)",
                    borderRadius: 10,
                    border: "1px solid var(--gray-3)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      Identification #{i + 1}
                    </div>
                    {form.identifications.length > 1 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: "var(--danger)" }}
                        onClick={() => removeId(i)}
                      >
                        ✕ Remove
                      </button>
                    )}
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.5fr",
                      gap: 12,
                      marginBottom: 12,
                    }}
                  >
                    <Field label="ID Type" required>
                      <select
                        className="form-control form-select"
                        value={id.type}
                        onChange={(e) => setId(i, "type", e.target.value)}
                      >
                        {[
                          "Driver License",
                          "Passport",
                          "State ID",
                          "Military ID",
                          "Other",
                        ].map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label={
                        id.type === "Social Security #"
                          ? "Social Security Number"
                          : "License / ID Number"
                      }
                      required
                    >
                      <input
                        className="form-control"
                        value={id.idNumber}
                        onChange={(e) => setId(i, "idNumber", e.target.value)}
                        placeholder={
                          id.type === "Social Security #"
                            ? "XXX-XX-XXXX"
                            : "e.g. 7537482"
                        }
                        style={{
                          fontFamily: "monospace",
                          fontWeight: 700,
                          letterSpacing: 1,
                        }}
                        maxLength={
                          id.type === "Social Security #" ? 11 : undefined
                        }
                      />
                      {id.type === "Social Security #" && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--gray-1)",
                            marginTop: 3,
                          }}
                        >
                          🔒 Stored securely. Format: XXX-XX-XXXX
                        </div>
                      )}
                    </Field>
                  </div>

                  {id.type !== "Social Security #" && (
                    <>
                      <div className="id-grid-4col">
                        <Field label="Issued State">
                          <select
                            className="form-control form-select"
                            value={id.issuedState}
                            onChange={(e) =>
                              setId(i, "issuedState", e.target.value)
                            }
                          >
                            {US_STATES.map((s) => (
                              <option key={s}>{s}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Class">
                          <input
                            className="form-control"
                            value={id.issuedClass}
                            onChange={(e) =>
                              setId(i, "issuedClass", e.target.value)
                            }
                            placeholder="D"
                          />
                        </Field>
                        <Field label="Issue Date">
                          <DateInput
                            value={id.issueDate}
                            onChange={(e) =>
                              setId(i, "issueDate", e.target.value)
                            }
                            minYear={1980}
                          />
                          {id.issueDate && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "var(--gray-1)",
                                marginTop: 2,
                                fontFamily: "monospace",
                              }}
                            >
                              Card: {isoToDisplay(id.issueDate)}
                            </div>
                          )}
                        </Field>
                        <Field label="Expiry Date">
                          <DateInput
                            value={id.expiryDate}
                            onChange={(e) =>
                              setId(i, "expiryDate", e.target.value)
                            }
                            minYear={2000}
                            maxYear={2045}
                          />
                          {id.expiryDate && (
                            <div
                              style={{
                                fontSize: 11,
                                marginTop: 2,
                                fontFamily: "monospace",
                                color:
                                  new Date(id.expiryDate) < new Date()
                                    ? "var(--danger)"
                                    : "var(--success)",
                                fontWeight: 600,
                              }}
                            >
                              Card: {isoToDisplay(id.expiryDate)}{" "}
                              {new Date(id.expiryDate) < new Date()
                                ? "⚠️ EXPIRED"
                                : "✅"}
                            </div>
                          )}
                        </Field>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 12,
                        }}
                      >
                        <Field label="Restrictions">
                          <input
                            className="form-control"
                            value={id.restrictions}
                            onChange={(e) =>
                              setId(i, "restrictions", e.target.value)
                            }
                            placeholder="NONE"
                          />
                        </Field>
                        <Field label="Endorsements">
                          <input
                            className="form-control"
                            value={id.endorsements}
                            onChange={(e) =>
                              setId(i, "endorsements", e.target.value)
                            }
                            placeholder="NONE"
                          />
                        </Field>
                      </div>
                    </>
                  )}
                </div>
              ))}

              <button
                className="btn btn-outline btn-sm"
                style={{ alignSelf: "flex-start" }}
                onClick={addId}
              >
                + Add Another ID
              </button>
            </div>
          </div>

          {/* SOCIAL SECURITY NUMBER */}
          <div className="card">
            <div className="card-body">
              <SectionHeader
                icon="🔒"
                title="Social Security Number"
                subtitle="Enter exactly as shown on the Social Security card"
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 2fr",
                  gap: 16,
                  alignItems: "start",
                }}
              >
                <Field label="SSN">
                  <input
                    className="form-control"
                    value={form.ssn}
                    onChange={(e) => {
                      // Auto-format as XXX-XX-XXXX
                      const digits = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      let formatted = digits;
                      if (digits.length > 5)
                        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
                      else if (digits.length > 3)
                        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                      set("ssn", formatted);
                    }}
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    style={{ fontFamily: "monospace", letterSpacing: 1.5 }}
                  />
                </Field>
                <div
                  style={{
                    paddingTop: 22,
                    fontSize: 12,
                    color: "var(--gray-1)",
                    lineHeight: 1.6,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      background: "var(--gray-3)",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontFamily: "monospace",
                      marginBottom: 4,
                      fontSize: 13,
                      letterSpacing: 1,
                    }}
                  >
                    XXX-XX-XXXX
                  </span>
                  <br />
                  🔐 Stored securely. Used for identity verification only.
                </div>
              </div>
            </div>
          </div>

          {/* PERSONAL INFO */}
          <div className="card">
            <div className="card-body">
              <SectionHeader
                icon="👤"
                title="Personal Information"
                subtitle="From the front of the Driver's License"
              />
              <div className="personal-grid-3">
                <Field label="First Name" required>
                  <input
                    className="form-control input-capitalize"
                    value={form.firstName}
                    onChange={(e) => set("firstName", e.target.value)}
                    placeholder=""
                  />
                </Field>
                <Field label="Middle Name">
                  <input
                    className="form-control"
                    value={form.middleName}
                    onChange={(e) => set("middleName", e.target.value)}
                    placeholder=""
                  />
                </Field>
                <Field label="Last Name" required>
                  <input
                    className="form-control input-capitalize"
                    value={form.lastName}
                    onChange={(e) => set("lastName", e.target.value)}
                    placeholder=""
                  />
                </Field>
              </div>

              <div className="personal-grid-5">
                <Field label="Date of Birth" required>
                  <DateInput
                    value={form.dateOfBirth}
                    onChange={(e) => set("dateOfBirth", e.target.value)}
                    minYear={1920}
                    maxYear={2010}
                  />
                  {form.dateOfBirth && (
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--gray-1)",
                        marginTop: 2,
                        fontFamily: "monospace",
                      }}
                    >
                      Card: {isoToDisplay(form.dateOfBirth)}
                    </div>
                  )}
                </Field>
                <Field label="Sex">
                  <select
                    className="form-control form-select"
                    value={form.sex}
                    onChange={(e) => set("sex", e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="M">M</option>
                    <option value="F">F</option>
                    <option value="Other">X</option>
                  </select>
                </Field>
                <Field label="Height">
                  <input
                    className="form-control"
                    value={form.height}
                    onChange={(e) => set("height", e.target.value)}
                    placeholder="5-06"
                  />
                </Field>
                <Field label="Weight (lbs)">
                  <input
                    className="form-control"
                    value={form.weight}
                    onChange={(e) => set("weight", e.target.value)}
                    placeholder="165"
                  />
                </Field>
                <Field label="Eye Color">
                  <select
                    className="form-control form-select"
                    value={form.eyeColor}
                    onChange={(e) => set("eyeColor", e.target.value)}
                  >
                    <option value="">—</option>
                    {EYE_COLORS.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="address-grid">
                <Field label="Hair Color">
                  <select
                    className="form-control form-select"
                    value={form.hairColor}
                    onChange={(e) => set("hairColor", e.target.value)}
                  >
                    <option value="">—</option>
                    {HAIR_COLORS.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          </div>

          {/* ADDRESS */}
          <div className="card">
            <div className="card-body">
              <SectionHeader
                icon="📍"
                title="Address"
                subtitle="Copy from Driver's License — exactly as shown"
              />
              <div className="employer-grid">
                <Field label="Street Address">
                  <input
                    className="form-control input-capitalize"
                    value={form.address.street}
                    onChange={(e) => setAddr("street", e.target.value)}
                    placeholder=""
                  />
                </Field>
                <Field label="Apt / Unit">
                  <input
                    className="form-control"
                    value={form.address.apt}
                    onChange={(e) => setAddr("apt", e.target.value)}
                    placeholder=""
                  />
                </Field>
              </div>
              <div className="employer-grid2">
                <Field label="City">
                  <input
                    className="form-control input-capitalize"
                    value={form.address.city}
                    onChange={(e) => setAddr("city", e.target.value)}
                    placeholder=""
                  />
                </Field>
                <Field label="State">
                  <select
                    className="form-control form-select"
                    value={form.address.state}
                    onChange={(e) => setAddr("state", e.target.value)}
                  >
                    {US_STATES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="ZIP Code">
                  <input
                    className="form-control"
                    value={form.address.zip}
                    onChange={(e) => setAddr("zip", e.target.value)}
                    placeholder=""
                  />
                </Field>
                <Field label="Country">
                  <input
                    className="form-control input-capitalize"
                    value={form.address.country}
                    onChange={(e) => setAddr("country", e.target.value)}
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* CONTACTS */}
          <div className="card">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <SectionHeader
                  icon="📞"
                  title="Contact Information"
                  subtitle="Phone, Email"
                />
                <button
                  className="btn btn-outline btn-sm"
                  onClick={addContact}
                  style={{ alignSelf: "flex-start" }}
                >
                  + Add
                </button>
              </div>
              {form.contacts.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-end",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ width: 160 }}>
                    <label className="form-label">Type</label>
                    <select
                      className="form-control form-select"
                      value={c.type}
                      onChange={(e) => setContact(i, "type", e.target.value)}
                    >
                      {[
                        "Home Phone",
                        "Cell Phone",
                        "Work Phone",
                        "Email",
                        "Other",
                      ].map((t) => (
                        <option key={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">Number / Value</label>
                    {c.type === "Email" ? (
                      <input
                        className="form-control"
                        value={c.value}
                        onChange={(e) => setContact(i, "value", e.target.value)}
                        placeholder="email@example.com"
                        type="email"
                      />
                    ) : (
                      <input
                        className="form-control"
                        value={c.value}
                        onChange={(e) => handlePhoneChange(i, e.target.value)}
                        placeholder="+1 (xxx) xxx-xxxx"
                        style={{ fontFamily: "monospace", letterSpacing: 0.5 }}
                      />
                    )}
                  </div>
                  {form.contacts.length > 1 && (
                    <button
                      className="btn btn-ghost btn-icon"
                      onClick={() => removeContact(i)}
                      style={{ color: "var(--danger)", paddingBottom: 10 }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <div
                style={{ fontSize: 11, color: "var(--gray-1)", marginTop: 4 }}
              >
                📌 Phone stored as:{" "}
                <span style={{ fontFamily: "monospace" }}>(251) 555-0123</span>
              </div>
            </div>
          </div>

          {/* EMPLOYER */}
          <div className="card">
            <div className="card-body">
              <SectionHeader
                icon="🏢"
                title="Employer Information"
                subtitle="Who issues the check? (from cheque maker field)"
              />
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                <Field label="Employer / Company Name">
                  <MasterDropdown
                    value={form.employer.name}
                    onChange={(val) => setEmpl("name", val)}
                    items={companyList}
                    placeholder="Select or type company name…"
                    icon="🏢"
                  />
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 5,
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--gray-1)" }}>
                      Company not found?
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        color: "var(--primary)",
                        fontWeight: 700,
                      }}
                      onClick={() => setShowAddCompany(true)}
                    >
                      ➕ Add New Company
                    </button>
                  </div>
                </Field>
                <Field label="Employer Phone">
                  <input
                    className="form-control"
                    value={form.employer.phone}
                    onChange={(e) => setEmpl("phone", e.target.value)}
                    placeholder="(xxx) xxx-xxxx"
                  />
                </Field>
                <Field label="Employer Address" span={2}>
                  <input
                    className="form-control"
                    value={form.employer.address}
                    onChange={(e) => setEmpl("address", e.target.value)}
                    placeholder=""
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* NOTES */}
          <div className="card">
            <div className="card-body">
              <SectionHeader icon="📝" title="Notes" />
              <textarea
                className="form-control"
                rows={3}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder="Any additional observations or notes about this customer..."
                style={{ resize: "vertical" }}
              />
            </div>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div
          className="customer-preview-panel"
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div
            className="card"
            style={{
              background: "var(--dark)",
              color: "white",
              border: "none",
            }}
          >
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.5)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Customer Preview
              </div>
            </div>
            <div style={{ padding: "18px" }}>
              {idPhotoPreview ? (
                <img
                  src={idPhotoPreview}
                  alt="ID"
                  style={{
                    width: "100%",
                    height: 110,
                    objectFit: "cover",
                    borderRadius: 8,
                    marginBottom: 14,
                    border: "2px solid rgba(255,255,255,0.1)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: 80,
                    background: "rgba(255,255,255,0.06)",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 14,
                    fontSize: 28,
                  }}
                >
                  👤
                </div>
              )}
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 18,
                  fontWeight: 800,
                  color: "white",
                  marginBottom: 4,
                }}
              >
                {[form.firstName, form.middleName, form.lastName]
                  .filter(Boolean)
                  .join(" ") || "Full Name"}
              </div>
              {form.dateOfBirth && (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.5)",
                    marginBottom: 3,
                  }}
                >
                  DOB: {isoToDisplay(form.dateOfBirth)} · {form.sex || "—"} ·{" "}
                  {form.height || "—"}
                </div>
              )}
              {id0.idNumber && (
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    marginTop: 8,
                    padding: "8px 10px",
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: 8,
                  }}
                >
                  <span
                    style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}
                  >
                    {id0.type}
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontWeight: 700,
                      color: "var(--accent)",
                      fontSize: 13,
                    }}
                  >
                    {id0.idNumber}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "rgba(255,255,255,0.5)",
                      marginLeft: "auto",
                    }}
                  >
                    {id0.issuedState} · Cls {id0.issuedClass}
                  </span>
                </div>
              )}
              {id0.expiryDate && (
                <div
                  style={{
                    fontSize: 11.5,
                    color:
                      new Date(id0.expiryDate) < new Date()
                        ? "var(--danger)"
                        : "var(--success)",
                    marginTop: 6,
                  }}
                >
                  {new Date(id0.expiryDate) < new Date()
                    ? "⚠️ ID EXPIRED"
                    : `✅ Expires: ${isoToDisplay(id0.expiryDate)}`}
                </div>
              )}
              {form.address.city && (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 10,
                  }}
                >
                  📍{" "}
                  {[form.address.street, form.address.apt]
                    .filter(Boolean)
                    .join(" ")}
                  <br />
                  {form.address.city}, {form.address.state} {form.address.zip}
                </div>
              )}
              {form.contacts[0]?.value && form.contacts[0].value !== "+1 " && (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 6,
                  }}
                >
                  📞 {form.contacts[0].value}
                </div>
              )}
              {form.employer.name && (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(255,255,255,0.55)",
                    marginTop: 6,
                  }}
                >
                  🏢 {form.employer.name}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid var(--gray-3)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--gray-1)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Intake Checklist
              </div>
            </div>
            <div
              style={{
                padding: "14px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {[
                {
                  label: "Full Name",
                  done: !!(form.firstName && form.lastName),
                },
                { label: "Date of Birth", done: !!form.dateOfBirth },
                {
                  label: "ID Number",
                  done: !!form.identifications[0]?.idNumber,
                },
                {
                  label: "ID Expiry Date",
                  done: !!form.identifications[0]?.expiryDate,
                },
                {
                  label: "Address",
                  done: !!(form.address.street && form.address.city),
                },
                {
                  label: "Phone Number",
                  done: !!(
                    form.contacts[0]?.value && form.contacts[0].value !== "+1 "
                  ),
                },
                { label: "ID Photo", done: !!idPhotoPreview },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background: item.done
                        ? "var(--success)"
                        : "var(--gray-3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: "white",
                      flexShrink: 0,
                    }}
                  >
                    {item.done ? "✓" : ""}
                  </div>
                  <span
                    style={{
                      color: item.done ? "var(--dark)" : "var(--gray-1)",
                      fontWeight: item.done ? 600 : 400,
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <button
            className="btn btn-primary btn-lg w-full"
            onClick={handleSubmit}
            disabled={loading || scanning}
          >
            {scanning ? (
              <span className="flex items-center gap-2">
                <span className="spinner" /> Scanning ID...
              </span>
            ) : loading ? (
              <span className="flex items-center gap-2">
                <span className="spinner" /> Saving...
              </span>
            ) : (
              "✅ Save Customer"
            )}
          </button>
          <button
            className="btn btn-outline w-full"
            onClick={() => navigate("/customers")}
          >
            Cancel
          </button>
          <div
            style={{
              fontSize: 11.5,
              color: "var(--gray-1)",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            After saving, you can immediately cash a check for this customer
            from their profile page.
          </div>
        </div>
      </div>
    </Layout>
  );
}
